import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { Paperclip, SendHorizontal } from 'lucide-react';
import { TURN_TEXT_MAX } from '@contracts/assistant';
import { useNarrowView } from '../../lib/media';
import { useAssistant } from './AssistantProvider';
import { PastePrompt } from './ingest/PastePrompt';
import { SplitOffer } from './ingest/SplitOffer';
import { UploadTray } from './ingest/UploadTray';
import { INGEST_ACCEPT, PASTE_ASK_OVER, type IngestInput } from './ingest/useIngestInput';

/** The textarea grows with its text up to this many lines, then scrolls. */
const MAX_LINES = 6;

const ICON_BUTTON = 'flex size-11 shrink-0 items-center justify-center rounded-[7px] text-[hsl(var(--h-muted-foreground))] '
  + 'transition-colors hover:bg-[hsl(var(--h-muted))] hover:text-[hsl(var(--h-card-foreground))] lg:size-8';

/**
 * The message box (mockup composer): Enter sends, Shift+Enter adds a line, a composing IME (Vietnamese Telex) never
 * sends. No focus border or ring (the standing rule). A refused send puts the text back. There is no Stop button
 * (D126): closing the panel leaves the turn, which the server finishes on its own (D70).
 * With `ingest` (S5 §7.1, D114) it also takes files: the 📎 pin, and a paste over 1 500 characters asks first.
 */
export function Composer({ composerRef, send, running, refusal, ingest }: {
  composerRef: RefObject<HTMLTextAreaElement | null>;
  send: (text: string) => Promise<'sent' | 'refused'>;
  running: boolean;
  refusal: { code: string; message: string } | null;
  /** Null where ingest is off or the person is read-only: no pin, no paste prompt, the chat placeholder. */
  ingest: IngestInput | null;
}) {
  const { feel, prefs } = useAssistant();
  const [draft, setDraft] = useState('');
  const [pasted, setPasted] = useState<string | null>(null);
  /** D133: where the caret was when the long paste was held, so "Bỏ qua" puts the text exactly there. */
  const pasteAt = useRef<[number, number]>([0, 0]);
  // D132: the phone sheet takes no drag, so its placeholder offers no drop zone.
  const narrow = useNarrowView();
  const [extracting, setExtracting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    const el = composerRef.current;
    if (el === null) return;
    el.style.height = 'auto';
    // jsdom has no layout (scrollHeight 0): the box keeps its one row there.
    if (el.scrollHeight <= 0) return;
    const line = Number.parseFloat(getComputedStyle(el).lineHeight) || 20;
    const chrome = el.offsetHeight - el.clientHeight + 12;
    el.style.height = `${Math.min(el.scrollHeight, line * MAX_LINES + chrome)}px`;
  }, [draft, composerRef]);

  const submit = () => {
    const text = draft.trim();
    if (text === '' || running) return;
    setDraft('');
    feel({ kind: 'typing', active: false });
    void send(text).then((r) => { if (r === 'refused') setDraft(text); });
  };

  const extract = () => {
    if (ingest === null || pasted === null || extracting) return;
    setExtracting(true);
    void ingest.extract(pasted).then((ok) => {
      setExtracting(false);
      if (ok) setPasted(null);
    });
  };
  const chat = () => {
    if (pasted === null) return;
    const text = pasted;
    const [start, end] = pasteAt.current;
    setPasted(null);
    // O1 (D133): a refused turn puts the paste back where it was pasted, keeping what was typed around it.
    void send(text).then((r) => {
      if (r === 'refused') setDraft((d) => d.slice(0, start) + text + d.slice(Math.min(end, d.length)));
    });
  };

  /** D133: dismissing the prompt never loses the paste: it goes into the box as a normal paste would have. */
  const dismissPaste = () => {
    if (pasted === null) return;
    const [start, end] = pasteAt.current;
    const next = draft.slice(0, start) + pasted + draft.slice(end);
    setPasted(null);
    setDraft(next);
    feel({ kind: 'typing', active: true });
    const caret = start + pasted.length;
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (el === null) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="flex flex-col gap-2 px-3 py-2.5">
      {ingest !== null && <UploadTray queue={ingest.queue} />}
      {ingest?.split != null && (
        <SplitOffer offer={ingest.split} busy={ingest.splitting} onAccept={ingest.acceptSplit} onDismiss={ingest.dismissSplit} />
      )}
      {ingest !== null && pasted !== null && (
        <PastePrompt busy={extracting || running} onExtract={extract} onDismiss={dismissPaste}
          onChat={pasted.trim().length <= TURN_TEXT_MAX ? chat : null} />
      )}
      {refusal !== null && (
        <p role="alert" className="rounded-[7px] border border-[hsl(var(--h-error)/0.45)] bg-[hsl(var(--h-error-soft))] px-2 py-1.5 text-xs text-[hsl(var(--h-error-fg))]">
          {refusal.message}
        </p>
      )}
      <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {ingest !== null && (
          <>
            <button type="button" aria-label="Ghim file" title="Ghim file" onClick={() => fileRef.current?.click()}
              className={ICON_BUTTON}>
              <Paperclip className="size-4" aria-hidden="true" />
            </button>
            <input ref={fileRef} type="file" multiple accept={INGEST_ACCEPT} hidden tabIndex={-1}
              onChange={(e) => {
                ingest.pick(Array.from(e.target.files ?? []));
                // The same file must be choosable again after it was removed or failed.
                e.target.value = '';
              }} />
          </>
        )}
        <textarea
          ref={composerRef}
          rows={1}
          value={draft}
          placeholder={ingest === null ? 'Hỏi hoặc ra lệnh…'
            : narrow ? `Nhắn cho ${prefs.personaName}…` : `Nhắn cho ${prefs.personaName}, hoặc thả file vào đây…`}
          aria-label={`Tin nhắn cho ${prefs.personaName}`}
          onPaste={(e) => {
            if (ingest === null) return;
            const text = e.clipboardData.getData('text');
            if (text.length <= PASTE_ASK_OVER) return;
            e.preventDefault();
            const at: [number, number] = [e.currentTarget.selectionStart, e.currentTarget.selectionEnd];
            if (pasted !== null) {
              // F3: a paste already held goes into the box first (never lost); the new caret moves past it.
              const [start, end] = pasteAt.current;
              const shift = pasted.length - (end - start);
              const moved = (p: number) => (p <= start ? p : p >= end ? p + shift : start + pasted.length);
              setDraft(draft.slice(0, start) + pasted + draft.slice(end));
              pasteAt.current = [moved(at[0]), moved(at[1])];
            } else {
              pasteAt.current = at;
            }
            setPasted(text);
          }}
          onChange={(e) => {
            setDraft(e.target.value);
            feel({ kind: 'typing', active: e.target.value !== '' });
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
            e.preventDefault();
            submit();
          }}
          className="min-w-0 flex-1 resize-none rounded-[7px] border border-[hsl(var(--h-border))] bg-transparent px-2 py-1.5 text-base text-[hsl(var(--h-card-foreground))] placeholder:text-[hsl(var(--h-muted-foreground))] focus:outline-none focus-visible:outline-none focus-visible:ring-0 lg:text-sm"
        />
        <button
          type="submit"
          aria-label="Gửi"
          title="Gửi"
          disabled={draft.trim() === '' || running}
          className="flex size-11 shrink-0 items-center justify-center rounded-[7px] bg-[hsl(var(--h-primary))] text-[hsl(var(--h-primary-foreground))] transition-opacity hover:opacity-90 disabled:opacity-40 lg:size-8"
        >
          <SendHorizontal className="size-4" aria-hidden="true" />
        </button>
      </form>
    </div>
  );
}
